import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, Mail, MapPin, Send, MessageCircle } from "lucide-react";
import { submitContact } from "@/lib/api";
import { CONTACT } from "@/constants/testIds";
import { toast, Toaster } from "sonner";
import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";

const SECTORS = ["Clínica estética", "Centro de belleza", "Franquicia", "Medicina estética", "Distribuidor", "Otro"];
const INTERESTS = [
  { v: "demo", l: "Solicitar demostración" },
  { v: "catalog", l: "Descargar catálogo" },
  { v: "quote", l: "Solicitar presupuesto" },
  { v: "info", l: "Información general" },
];

export default function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    sector: "Clínica estética", message: "",
    interest: params.get("intent") || "demo",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const modelo = params.get("modelo");
    const intent = params.get("intent");
    if (modelo) {
      setForm((f) => ({ ...f, message: `Estoy interesado/a en el modelo ${modelo.toUpperCase()}.` }));
    }
    if (intent) setForm((f) => ({ ...f, interest: intent }));
  }, [params]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await submitContact(form);
      setSent(true);
      toast.success("Solicitud enviada. Nuestro equipo te contactará en menos de 24h.");
      setForm({ name: "", email: "", phone: "", company: "", sector: "Clínica estética", message: "", interest: "demo" });
    } catch (err) {
      toast.error("Hubo un error. Inténtalo de nuevo o escríbenos a info@asirox.com");
    } finally {
      setSending(false);
    }
  };

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const fieldCls = "w-full mt-2 bg-white border border-black/15 px-4 py-3 text-black focus:border-[#ff9c00] focus:ring-1 focus:ring-[#ff9c00] outline-none transition";

  return (
    <div className="pb-24">
      <SEO
        title="Contacto Asirox · Solicita Demo de Equipos Dermo Faciales"
        description="Solicita demo, catálogo o presupuesto de equipos dermo faciales Asirox. Respuesta técnico-comercial en menos de 24h con análisis ROI personalizado. WhatsApp +34 935 70 49 54. Sede Sabadell, Barcelona."
        keywords="contacto Asirox, demo equipo dermo facial, presupuesto plataforma dermo facial, comprar equipo dermo facial, catálogo equipos dermo faciales, distribuidor aparatología estética, Asirox Sabadell Barcelona"
      />
      <Toaster theme="light" position="bottom-right" />
      <PageHero
        overline="Contacto"
        title="Hablemos de tu"
        highlight="próximo equipo."
        subtitle="Nuestro equipo técnico-comercial te contactará en menos de 24h con un análisis personalizado para tu centro: modelo recomendado, ROI estimado y plan de implantación."
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 mt-8">
        <div className="lg:col-span-5">
          <div className="space-y-6">
            <div className="flex gap-4 items-start">
              <Phone size={18} className="text-[#3D1F0A] mt-1" strokeWidth={1.6}/>
              <div>
                <p className="overline">Teléfono</p>
                <a href="tel:+34935704954" className="text-black font-bold mt-1 block link-underline">+34 935 70 49 54</a>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <Mail size={18} className="text-[#3D1F0A] mt-1" strokeWidth={1.6}/>
              <div>
                <p className="overline">Email</p>
                <a href="mailto:info@asirox.com" className="text-black font-bold mt-1 block link-underline">info@asirox.com</a>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <MapPin size={18} className="text-[#3D1F0A] mt-1" strokeWidth={1.6}/>
              <div>
                <p className="overline">Sede central</p>
                <p className="text-black mt-1">Calle Rocafort 89,<br/>08205 Sabadell, Barcelona, España</p>
              </div>
            </div>
          </div>

          <a
            href="https://wa.me/34935704954?text=Hola%20Asirox%2C%20quiero%20información%20sobre%20vuestras%20plataformas%20dermo%20faciales"
            target="_blank" rel="noreferrer"
            data-testid={CONTACT.whatsapp}
            className="mt-10 flex items-center gap-3 border border-[#ff9c00]/60 bg-white px-6 py-4 hover:bg-[#ff9c00]/5 transition"
          >
            <MessageCircle size={20} className="text-[#3D1F0A]" strokeWidth={1.6}/>
            <div>
              <p className="text-sm text-black font-bold">WhatsApp directo</p>
              <p className="text-xs text-black/60 font-mono">Respuesta &lt; 1h en horario comercial</p>
            </div>
          </a>

          <div className="mt-10 relative aspect-[4/3] border border-black/15 overflow-hidden">
            <iframe
              title="Asirox Sabadell"
              src="https://www.google.com/maps?q=Calle+Rocafort+89,+Sabadell,+Barcelona&output=embed"
              className="absolute inset-0 w-full h-full"
              loading="lazy"
            />
          </div>
        </div>

        <div className="lg:col-span-7">
          <form data-testid={CONTACT.form} onSubmit={handleSubmit} className="border border-black/15 bg-white p-8 md:p-10">
            <p className="overline mb-8">Formulario premium · Respuesta &lt; 24h</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="overline">Nombre completo *</label>
                <input data-testid={CONTACT.name} required value={form.name} onChange={setF("name")} className={fieldCls}/>
              </div>
              <div>
                <label className="overline">Email *</label>
                <input type="email" required data-testid={CONTACT.email} value={form.email} onChange={setF("email")} className={fieldCls}/>
              </div>
              <div>
                <label className="overline">Teléfono</label>
                <input data-testid={CONTACT.phone} value={form.phone} onChange={setF("phone")} className={fieldCls}/>
              </div>
              <div>
                <label className="overline">Empresa / Centro</label>
                <input data-testid={CONTACT.company} value={form.company} onChange={setF("company")} className={fieldCls}/>
              </div>
              <div>
                <label className="overline">Sector</label>
                <select data-testid={CONTACT.sector} value={form.sector} onChange={setF("sector")} className={fieldCls}>
                  {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="overline">Tipo de solicitud</label>
                <select data-testid={CONTACT.interest} value={form.interest} onChange={setF("interest")} className={fieldCls}>
                  {INTERESTS.map((i) => <option key={i.v} value={i.v}>{i.l}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="overline">Mensaje *</label>
                <textarea required rows={5} data-testid={CONTACT.message} value={form.message} onChange={setF("message")} className={`${fieldCls} resize-none`} placeholder="Cuéntanos sobre tu centro, tipos de piel atendidos, volumen estimado..."/>
              </div>
            </div>

            <p className="text-xs text-black/60 mt-6">
              Al enviar este formulario aceptas la política de privacidad de Asirox. Tus datos serán utilizados exclusivamente para responder a tu consulta.
            </p>

            <button
              type="submit"
              data-testid={CONTACT.submit}
              disabled={sending || sent}
              className="mt-8 btn-primary w-full md:w-auto disabled:opacity-50"
            >
              {sending ? "Enviando..." : sent ? "✓ Enviado" : <>Enviar solicitud <Send size={16}/></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
